import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtTL, fmtTLx, fmtMoney } from "@/lib/api";
import { Wallet, TrendingDown, CalendarX, LogOut, Building2, Loader2, Palmtree } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const sumMixed = (items, d) => {
  if (d?.currency === "USD" && d.fx) return fmtMoney(items.reduce((s, it) => s + (it.currency === "USD" ? it.amount : it.amount / d.fx), 0), "USD");
  const t = items.reduce((m, it) => { m[it.currency || "TRY"] = (m[it.currency || "TRY"] || 0) + it.amount; return m; }, {});
  const parts = Object.entries(t).filter(([, v]) => v).map(([c, v]) => fmtMoney(v, c));
  return parts.length ? parts.join(" + ") : fmtTL(0);
};
const fmtOdenecek = (d) => {
  if (d.currency !== "USD") return fmtTL(d.odenecek);
  return fmtMoney(d.net_usd ?? d.salary_net, "USD");
};
const fmtItem = (it, d) => (it.currency === "USD" ? fmtMoney(it.amount, "USD") : fmtTLx(it.amount, d));

export default function PersonnelPortal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/portal/me")
      .then((r) => setData(r.data))
      .catch(() => navigate("/personel-giris", { replace: true }));
  }, [navigate]);

  const logout = async () => {
    try { await api.post("/portal/logout"); } catch (e) {}
    navigate("/personel-giris");
  };

  if (!data)
    return (
      <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white">
      <div className="max-w-md mx-auto px-5 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center font-heading font-bold text-lg">
              {data.name[0]}
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">{data.name}</div>
              <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {data.branch_name} {data.role ? `· ${data.role}` : ""}
              </div>
            </div>
          </div>
          <button data-testid="portal-logout-button" onClick={logout} className="text-slate-500 hover:text-rose-400 transition-colors">
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Ödenecek tutar */}
        <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 p-6 shadow-lg shadow-emerald-900/40" data-testid="portal-odenecek">
          <div className="text-xs uppercase tracking-widest text-emerald-100/80">Bu Ay Ödenecek Tutar</div>
          <div className="mt-2 text-4xl font-heading font-extrabold tabular" data-testid="portal-odenecek-value">{fmtOdenecek(data)}</div>
          <div className="mt-3 flex justify-between text-sm text-emerald-100/90">
            <span>Alacak: <b className="tabular">{sumMixed(data.alacak_items, data)}</b></span>
            <span>Kesinti: <b className="tabular">-{sumMixed(data.verecek_items, data)}</b></span>
          </div>
        </div>

        {/* Devamsızlık */}
        <div className="rounded-2xl bg-slate-900/80 border border-slate-800 p-5 flex items-center gap-4" data-testid="portal-absence">
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center">
            <CalendarX className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Devamsızlık (Bu Ay)</div>
            <div className="text-2xl font-heading font-bold tabular">{data.absent_days} gün</div>
          </div>
        </div>

        {/* Alacaklarım */}
        <div className="rounded-2xl bg-slate-900/80 border border-slate-800 p-5" data-testid="portal-alacak">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-semibold">Alacaklarım</span>
          </div>
          <div className="space-y-2">
            {data.alacak_items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-slate-400">{it.label}</span>
                <span className="text-emerald-400 font-mono tabular">+{fmtItem(it, data)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm border-t border-slate-800 pt-2">
              <span className="font-semibold">Toplam Alacak</span>
              <span className="font-heading font-bold tabular">{sumMixed(data.alacak_items, data)}</span>
            </div>
          </div>
        </div>

        {/* Vereceklerim */}
        <div className="rounded-2xl bg-slate-900/80 border border-slate-800 p-5" data-testid="portal-verecek">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="w-4 h-4 text-rose-400" />
            <span className="text-sm font-semibold">Kesintilerim</span>
          </div>
          <div className="space-y-2">
            {data.verecek_items.length === 0 && (
              <div className="text-sm text-slate-500">Kesinti bulunmuyor.</div>
            )}
            {data.verecek_items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-slate-400">{it.label}</span>
                <span className="text-rose-400 font-mono tabular">-{fmtItem(it, data)}</span>
              </div>
            ))}
            {data.verecek_items.length > 0 && (
              <div className="flex justify-between text-sm border-t border-slate-800 pt-2">
                <span className="font-semibold">Toplam Kesinti</span>
                <span className="font-heading font-bold tabular">-{sumMixed(data.verecek_items, data)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Yıllık İzin */}
        {data.leave && (
          <div className="rounded-2xl bg-slate-900/80 border border-slate-800 p-5" data-testid="portal-leave">
            <div className="flex items-center gap-2 mb-3">
              <Palmtree className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold">Yıllık İzin Durumum</span>
            </div>
            {data.leave.entitlement === 0 ? (
              <div className="text-sm text-slate-500">
                {data.leave.years === null ? "İşe giriş tarihiniz tanımlı değil." : "1 yılı doldurunca yıllık izin hakkı kazanırsınız."}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-xl bg-slate-800/60 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Hak</div>
                    <div className="text-lg font-heading font-bold tabular" data-testid="portal-leave-entitlement">{data.leave.entitlement}</div>
                  </div>
                  <div className="rounded-xl bg-slate-800/60 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Kullanılan</div>
                    <div className="text-lg font-heading font-bold text-amber-400 tabular" data-testid="portal-leave-used">{data.leave.used}</div>
                  </div>
                  <div className="rounded-xl bg-slate-800/60 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Kalan</div>
                    <div className="text-lg font-heading font-bold text-emerald-400 tabular" data-testid="portal-leave-remaining">{data.leave.remaining}</div>
                  </div>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, Math.round((data.leave.used / data.leave.entitlement) * 100))}%` }} />
                </div>
                {data.leave.records.length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid="portal-leave-records">
                    {data.leave.records.map((r) => (
                      <div key={r.id} className="flex justify-between text-xs">
                        <span className="text-slate-400 tabular">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                        <span className="text-slate-500">{r.days} gün{r.description ? ` · ${r.description}` : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="text-center text-[11px] text-slate-600 pb-4">
          VİLAYET Personel Portalı · Prim, avans ve devamsızlık ay sonunda sıfırlanır
        </div>
      </div>
    </div>
  );
}
